// Story 10.3 — Página principal de conversaciones por bot. Layout split en
// desktop (lista izquierda + detalle derecha); mobile alterna por presencia
// de `?c=`.

import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useMemo } from 'react';
import { ConversationDetail } from '@/components/conversations/ConversationDetail';
import { ConversationFilters } from '@/components/conversations/ConversationFilters';
import { ConversationList } from '@/components/conversations/ConversationList';
import { EmptyConversations } from '@/components/conversations/EmptyConversations';
import { useBot } from '@/hooks/queries/use-bots';
import { useConversations } from '@/hooks/queries/use-conversations';
import type { ConversationFilters as Filters } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

export function ConversationsPage(): React.ReactElement {
  const params = useParams({ strict: false }) as { botId?: string };
  const botId = Number(params.botId);
  const search = useSearch({ strict: false }) as {
    c?: string;
    since?: string;
    until?: string;
    flow?: string;
    status?: 'active' | 'expired' | 'completed';
    q?: string;
  };
  const navigate = useNavigate();
  const { data: bot } = useBot(botId, Number.isFinite(botId));

  const filters: Filters = useMemo(
    () => ({
      since: search.since,
      until: search.until,
      flow: search.flow,
      status: search.status,
      q: search.q,
    }),
    [search.since, search.until, search.flow, search.status, search.q],
  );

  const list = useConversations(botId, filters);
  const conversations = useMemo(() => list.data?.pages.flatMap((p) => p.data) ?? [], [list.data]);
  const total = list.data?.pages[0]?.pagination.total ?? 0;

  const selectedSessionId = search.c ? Number(search.c) : null;

  function updateSearch(next: Partial<typeof search>): void {
    navigate({
      to: '/bots/$botId/conversations',
      params: { botId: String(botId) },
      search: (prev) => ({ ...(prev as object), ...next }),
    });
  }

  function handleSelect(sessionId: number): void {
    updateSearch({ c: String(sessionId) });
  }

  function handleBack(): void {
    updateSearch({ c: undefined });
  }

  function handleFiltersChange(next: Filters): void {
    updateSearch({ ...next });
  }

  // Empty state: bot existe pero sin sesiones todavía y sin filtros activos.
  const isFirstPageEmpty = !list.isLoading && conversations.length === 0;
  const hasActiveFilters = Object.values(filters).some((v) => v !== undefined && v !== '');
  if (isFirstPageEmpty && !hasActiveFilters) {
    return <EmptyConversations phoneNumberId={bot?.phoneNumberId ?? null} />;
  }

  const showDetail = selectedSessionId !== null;

  return (
    <div className="flex h-[calc(100vh-8rem)] flex-col gap-3">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Conversaciones</h1>
        <p className="text-sm text-muted-foreground">
          {total} {total === 1 ? 'conversación' : 'conversaciones'}
          {list.data?.pages[0]?.partial && ' · datos parciales (event_log no habilitado)'}
        </p>
      </div>
      <ConversationFilters filters={filters} onChange={handleFiltersChange} />

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 md:grid-cols-[minmax(280px,360px)_1fr]">
        <aside
          className={cn(
            'min-h-0 overflow-y-auto rounded-md border border-border bg-card',
            showDetail ? 'hidden md:block' : 'block',
          )}
        >
          <ConversationList
            conversations={conversations}
            selectedSessionId={selectedSessionId}
            onSelect={handleSelect}
            isLoading={list.isLoading}
            hasNextPage={Boolean(list.hasNextPage)}
            onLoadMore={() => list.fetchNextPage()}
            isFetchingMore={list.isFetchingNextPage}
          />
        </aside>
        <section
          className={cn(
            'min-h-0 overflow-hidden rounded-md border border-border bg-background',
            showDetail ? 'block' : 'hidden md:block',
          )}
        >
          {selectedSessionId !== null ? (
            <ConversationDetail botId={botId} sessionId={selectedSessionId} onBack={handleBack} />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Selecciona una conversación para ver el detalle.
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
